import { useState } from "react";
import "./PreferencesPanel.styles.scss";

// Context
import { useAppContext } from "@/context/context";

// Component Imports
import { AutocompleteInput, DateTimePickerInput } from "@/components/Inputs";

// Type Imports
import {
  IUserPreferencesState,
  PreferencesState,
} from "@pulse-ui/types/components/ToolPanel";
import { ISelectOption } from "@pulse-ui/types/components/DropdownInput";

// AG Grid Imports
import { CustomToolPanelProps } from "ag-grid-react";
import { GridState } from "ag-grid-community";

// Material UI Imports
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
} from "@mui/material";
import SettingsIcon from "@mui/icons-material/Settings";
import RefreshIcon from "@mui/icons-material/Refresh";
import SaveIcon from "@mui/icons-material/Save";
import RestartAltIcon from "@mui/icons-material/RestartAlt";

// Util Imports
import {
  deleteUserState,
  saveUserState,
  setIntervalPreferenceInContext,
  setQuoteAssetPreferenceInContext,
  setStartTimePreferenceInContext,
} from "../../../utils/promises";

// Package Imports
import { v4 as uuidv4 } from "uuid";
import { IAlertMessage } from "@pulse-ui/types/components/AlertMessage";
import dayjs, { Dayjs } from "dayjs";

export interface PreferencesPanelProps extends CustomToolPanelProps {
  title: string; // Title displayed at the top of the panel
  titleIcon: "preferences"; // Icon to display next to the title
  refdataAssets: ISelectOption[]; // Refdata Assets
  PREFERENCES_STATE_ID: string; // Preferences State ID
  USDT: "USDT"; // USDT Const
  ONEDAY: "ONEDAY"; // ONEDAY Const
  fetchUserState: () => Promise<IUserPreferencesState | null>; // Fetch User Time State
  setLoading: React.Dispatch<React.SetStateAction<boolean>>; // Loading state setter
  pushAlert: (alert: IAlertMessage) => void; // Alert message handler
  overviewDataHandler: (
    quoteAsset: string,
    start_from_time_picker_value: Dayjs | null,
    interval_value: string | null
  ) => Promise<{
    status: number;
    message: any;
  }>; // Overview data handler
  convertStartTimeParamToDayjs: (
    start_time: string | null
  ) => dayjs.Dayjs | null; // Convert Start Time Param to Dayjs
}

export default ({
  title,
  titleIcon,
  refdataAssets,
  PREFERENCES_STATE_ID,
  USDT,
  ONEDAY,
  fetchUserState,
  setLoading,
  pushAlert,
  overviewDataHandler,
  convertStartTimeParamToDayjs,
}: PreferencesPanelProps) => {
  // STATES - START ---------------------------------------------------------
  const DEFAULT = "DEFAULT";
  const ONEBUSINESSDAY = "onebusinessday";
  const location_origin = window.location.origin;
  const [deleteViewModal, setDeleteViewModal] = useState<{
    open: boolean;
    grid_id: string | null;
    display_name: string | null;
  }>({
    open: false,
    grid_id: null,
    display_name: null,
  });
  const startFromIntervalOptions: ISelectOption[] = [
    { label: "1 Day", value: ONEDAY },
    { label: "1 Business Day", value: ONEBUSINESSDAY, disabled: true },
  ];

  // Context States
  const { context_state, set_user_preferences } = useAppContext();
  // STATES - END -----------------------------------------------------------

  // METHODS - START ---------------------------------------------------------
  /**
   * Saves the current configuration state with a given name.
   * Handles the entire saving process including API communication and state updates.
   *
   * @async
   * @function saveCurrentGridState
   * @param {void}
   * @returns {Promise<void>}
   *
   * @example
   * await saveCurrentState();
   *
   * @throws {Error} Pushes an alert message if saving fails
   */
  const saveCurrentState = async () => {
    try {
      await removeUserState(PREFERENCES_STATE_ID);

      let state: GridState | PreferencesState | null = null;
      let { quote_asset, interval, start_time } =
        context_state.user_preferences;

      state = {
        quote_asset: quote_asset || USDT,
        interval: interval || ONEDAY,
        start_time: start_time || null,
      };

      if (state) {
        // Prepare the state data for saving
        const stateData = {
          grid_id: PREFERENCES_STATE_ID,
          display_name: "DEFAULT",
          grid_state: JSON.stringify(state),
        };

        // Save to backend
        await saveUserState(location_origin, stateData);

        // Refresh the list of saved states for associated type
        await fetchUserState();
      }
    } catch (error_res: any) {
      pushAlert({
        id: uuidv4(),
        message: error_res.message,
        status: error_res?.status || 500,
      });
    }
  };

  /**
   * Deletes a saved time state configuration.
   *
   * @async
   * @function removeUserState
   * @param {string} grid_id - The unique identifier of the state to remove
   * @returns {Promise<void>}
   *
   * @example
   * await removeState("grid-123");
   *
   * @throws {Error} Pushes an alert message if deletion fails
   */
  const removeUserState = async (grid_id: string) => {
    setLoading(true);

    try {
      // Validate input parameters
      if (grid_id && grid_id.length > 0) {
        // Delete the time state
        const display_name = DEFAULT;
        await deleteUserState(location_origin, { grid_id, display_name });

        // Refresh the list of saved states
        await fetchUserState();
      }
    } catch (error_res: any) {
      pushAlert({
        id: uuidv4(),
        message: error_res.message,
        status: error_res?.status || 500,
      });
    } finally {
      handleCloseDeleteViewModal();
      setLoading(false);
    }
  };

  const handleCloseDeleteViewModal = () => {
    setDeleteViewModal({
      open: false,
      grid_id: null,
      display_name: null,
    });
  };

  const handleOpenDeleteViewModal = (grid_id: string, display_name: string) => {
    setDeleteViewModal({
      open: true,
      grid_id,
      display_name,
    });
  };

  const generateTitleIcon = (icon: "preferences") => {
    switch (icon) {
      case "preferences":
        return <SettingsIcon />;
      default:
        return null;
    }
  };

  const handleQuoteAssetAutocompleteDropdownChange = async (
    _: React.SyntheticEvent,
    newValue: ISelectOption | null
  ) => {
    try {
      if (newValue) {
        await setQuoteAssetPreferenceInContext(
          newValue.value,
          set_user_preferences
        );
      } else {
        await setQuoteAssetPreferenceInContext(USDT, set_user_preferences);
      }
    } catch (error_res: any) {
      pushAlert({
        id: uuidv4(),
        message: error_res.error,
        status: error_res?.status || 500,
      });
    }
  };

  const handleStartFromIntervalAutocompleteDropdownChange = async (
    _: React.SyntheticEvent,
    newValue: ISelectOption | null
  ) => {
    try {
      if (newValue) {
        await setIntervalPreferenceInContext(
          newValue.value,
          set_user_preferences
        );
      } else {
        await setIntervalPreferenceInContext(ONEDAY, set_user_preferences);
      }
    } catch (error_res: any) {
      pushAlert({
        id: uuidv4(),
        message: error_res.error,
        status: error_res?.status || 500,
      });
    }
  };

  const handleDateTimePickerChange = async (date: Dayjs | null) => {
    try {
      if (date) {
        await setStartTimePreferenceInContext(
          `${date.hour()}-${date.minute()}`,
          set_user_preferences
        );
      } else {
        await setStartTimePreferenceInContext(null, set_user_preferences);
      }
    } catch (error_res: any) {
      pushAlert({
        id: uuidv4(),
        message: error_res.error,
        status: error_res?.status || 500,
      });
    }
  };
  // METHODS - END -----------------------------------------------------------

  return (
    <div className="preferences-panel-container">
      <div className="preferences-panel-title pulse-mt-1 pulse-mb-2">
        {generateTitleIcon(titleIcon)}
        <p className="pulse-typography-subtitle-1 pulse-color--background-100 pulse-ml-1">
          {title}
        </p>
      </div>

      <div className="preferences-panel-buttons-container pulse-mt-1">
        <Button
          className="reload-button"
          onClick={() =>
            overviewDataHandler(
              context_state.user_preferences.quote_asset || USDT,
              convertStartTimeParamToDayjs(
                context_state.user_preferences.start_time
              ),
              context_state.user_preferences.interval || ONEDAY
            )
          }
          size="small"
          disabled={
            !context_state.user_preferences.interval ||
            context_state.user_preferences.interval.length === 0
          }
          endIcon={<RefreshIcon />}
        >
          Reload
        </Button>

        <Divider
          className="preferences-panel-divider"
          sx={{ margin: "0.75rem 0" }}
        />

        <Button
          className="save-button"
          onClick={saveCurrentState}
          size="small"
          disabled={
            !context_state.user_preferences.interval ||
            context_state.user_preferences.interval.length === 0
          }
          endIcon={<SaveIcon />}
        >
          Save
        </Button>

        <Button
          className="reset-button"
          onClick={() =>
            handleOpenDeleteViewModal(PREFERENCES_STATE_ID, DEFAULT)
          }
          size="small"
          disabled={
            !context_state.user_preferences.interval ||
            context_state.user_preferences.interval.length === 0
          }
          endIcon={<RestartAltIcon />}
        >
          Reset
        </Button>
      </div>

      <div className="preferences-panel-inputs-container pulse-mt-1">
        <Divider
          className="preferences-panel-divider"
          sx={{ marginBottom: "0.75rem" }}
        />

        <AutocompleteInput
          value={{
            label: context_state.user_preferences.quote_asset || USDT,
            value: context_state.user_preferences.quote_asset || USDT,
          }}
          inputLabel="Quote Asset"
          onChange={handleQuoteAssetAutocompleteDropdownChange}
          disabled={refdataAssets.length < 1}
          options={refdataAssets}
        />

        <Divider
          className="preferences-panel-divider"
          sx={{ marginBottom: "0.75rem" }}
        />

        <AutocompleteInput
          value={
            startFromIntervalOptions.find(
              (option) =>
                option.value === context_state.user_preferences.interval
            ) || startFromIntervalOptions[0]
          }
          inputLabel="Interval"
          onChange={handleStartFromIntervalAutocompleteDropdownChange}
          disabled={startFromIntervalOptions.length < 1}
          options={startFromIntervalOptions}
        />

        <Divider
          className="preferences-panel-divider"
          sx={{ marginBottom: "0.75rem" }}
        />

        <DateTimePickerInput
          inputLabel="Start From Time"
          startFromTimePickerValue={convertStartTimeParamToDayjs(
            context_state.user_preferences.start_time
          )}
          handleChange={(date: Dayjs | null) =>
            handleDateTimePickerChange(date)
          }
        />

        <Divider className="preferences-panel-divider" />
      </div>

      <Dialog onClose={handleCloseDeleteViewModal} open={deleteViewModal.open}>
        <DialogTitle
          id="alert-dialog-title"
          sx={{ backgroundColor: "#e8eaef", color: "#091229" }}
        >
          {`Reset: ${deleteViewModal.display_name}`}
        </DialogTitle>
        <DialogContent sx={{ backgroundColor: "#e8eaef" }}>
          <DialogContentText
            id="alert-dialog-description"
            sx={{ color: "#091229" }}
          >
            Are you sure you want to reset the saved preferences? This cannot be
            undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ backgroundColor: "#e8eaef" }}>
          <Button
            onClick={handleCloseDeleteViewModal}
            sx={{ color: "#091229" }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => removeUserState(deleteViewModal?.grid_id || "")}
            sx={{ color: "#fe3c46", fontWeight: "bold" }}
          >
            Reset
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};
